import { MouseEvent, useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { Button } from '../../../components/Button'
import { Header } from '../../../components/Header'
import { Products } from '../../../components/Products'
import { RenderIf } from '../../../components/RenderIf'
import { ContextMenu } from '../../../components/ContextMenu'
import { NotFoundMessage } from '../../../components/commonStyles'

import { IProduct } from 'featureSliced/shared/store/reducers/CategoriesReducer/types'
import { useTranslations } from '../../shared/hooks/useTranslations'
import { useMySelector } from '../../shared/hooks/useMySelector'

import basket from 'assets/basket_small.svg'
import trashCan from 'assets/trashCan.svg'

import {
  WishlistContainer,
  WishlistCaption,
  MenuButtonContainer,
} from './styled'
import { useOpenProduct } from 'featureSliced/shared/hooks/openModals/useOpenProductModal'
import { WishlistProductType } from 'featureSliced/shared/store/reducers/WishlistReducer/types'
import { ProductTemplateIdType } from 'featureSliced/shared/store/reducers/QRMenuBucketReducer/types'
import { WishlistSlice } from 'featureSliced/shared/store/reducers/WishlistReducer'
import { useMyDispatch } from 'featureSliced/shared/hooks/useMyDispatch'

export type IPosition = {
  x: number
  y: number
}

export const Wishlist = () => {
  // Hooks
  const t = useTranslations()
  const navigate = useNavigate()
  const openProductModal = useOpenProduct()
  const dispatch = useMyDispatch()
  const addOrRemoveFromWishlist = WishlistSlice.actions.addOrRemoveFromWishlist
  const handleAddClick = (product: IProduct) => {
    openProductModal(product, product.categoryId)
  }

  const [searchParams] = useSearchParams()
  const table = searchParams.get('table')

  // Store
  const { wishlist = [] } = useMySelector((state) => state)
  console.log(' wishlist:', wishlist)
  const products = useMySelector((state) =>
    state.categories.items.flatMap((cat) => cat.products)
  )

  // State
  // const [selectedProducts, setSelectedProducts] = useState<
  //   ITemplateItem[] | IProduct[]
  // >([])
  const [selectedProducts, setSelectedProducts] = useState<IProduct[]>([])
  const [selectedProductId, setSelectedProductId] = useState<number>(0)
  const [position, setPosition] = useState<IPosition | null>(null)

  // useEffect
  // useEffect(() => {
  //   const wishlistSet = new Set(wishlist)
  //   setSelectedProducts(
  //     products
  //       ?.flatMap((product) => {
  //         const matchedItems = []
  //         if (wishlistSet.has(product.id)) {
  //           matchedItems.push(product)
  //         }
  //         const foundSTItems = product.innerCategoryItems?.filter((stItem) =>
  //           wishlistSet.has(stItem.id)
  //         )
  //         if (foundSTItems) {
  //           matchedItems.push(...foundSTItems)
  //         }
  //         return matchedItems
  //       })
  //       .filter(
  //         (item, index, self) =>
  //           wishlistSet.has(item.id) &&
  //           self.findIndex((i) => i.id === item.id) === index
  //       )
  //   )
  // }, [products, wishlist])

  useEffect(() => {
    const wishlistKeySet = new Set(
      wishlist?.map((item) => `${item.id}_${item.productTemplateId}`)
    )

    setSelectedProducts(
      products
        ?.flatMap((product) => {
          const matchedItems: IProduct[] = []

          const productKey = `${product.id}_${product.categoryId}`
          if (wishlistKeySet.has(productKey)) {
            matchedItems.push(product)
          }

          const foundSTItems = product.innerCategoryItems?.filter((stItem) => {
            const key = `${stItem.id}_${stItem.categoryId}`
            return wishlistKeySet.has(key)
          })

          if (foundSTItems?.length) {
            matchedItems.push(...foundSTItems)
          }

          return matchedItems
        })
        .filter((item, index, self) => {
          const key = `${item.id}_${item.categoryId}`
          return (
            wishlistKeySet.has(key) &&
            self.findIndex(
              (i) => i.id === item.id && i.categoryId === item.categoryId
            ) === index
          )
        }) || []
    )
  }, [products, wishlist])

  // Functions
  const handleProductClick = (
    product: IProduct,
    e: MouseEvent<HTMLElement>
  ) => {
    setSelectedProductId(product.id)
    setPosition({
      // x: e.pageX,
      x: e.pageX + 145 > window.innerWidth ? e.pageX - 125 : e.pageX,
      y: e.pageY,
    })
  }

  const navigateToMenu = () => {
    if (table) {
      navigate(`/menu?table=${table}`)
    }
    if (!table) {
      navigate('/menu')
    }
  }

  return (
    <WishlistContainer>
      <Header />
      <div>
        <WishlistCaption>{t('common.wishlist')}</WishlistCaption>
        <RenderIf condition={Boolean(selectedProducts?.length)}>
          <Products
            products={selectedProducts}
            onProductClick={handleProductClick}
          />
        </RenderIf>
        <RenderIf condition={Boolean(!selectedProducts?.length)}>
          <NotFoundMessage>
            <p>{t('errors.emptyWishlist')}</p>
            <p>{t('errors.wishlistBrowse')}</p>
          </NotFoundMessage>
          <MenuButtonContainer>
            <Button buttonType="common" onClick={navigateToMenu}>
              {t('errors.browseNow')}
            </Button>
          </MenuButtonContainer>
        </RenderIf>
      </div>
      <RenderIf condition={!!position}>
        <ContextMenu
          position={position as IPosition}
          discardPosition={() => setPosition(null)}
          items={contextMenuItems(
            t,
            selectedProducts,
            selectedProductId,
            handleAddClick,
            // TODO: check if it works correctly
            (product) => dispatch(addOrRemoveFromWishlist(product))
          )}
        />
      </RenderIf>
    </WishlistContainer>
  )
}

const contextMenuItems = (
  t: any,
  selectedProducts: IProduct[],
  selectedProductId: number,
  handleAddClick: (product: IProduct) => void,
  handleWishlist: (product: WishlistProductType) => void
) => {
  const selectedProduct = selectedProducts?.find(
    (product) => product.id === selectedProductId
  )
  return [
    {
      component: (
        <>
          <img src={basket} alt="Add" />
          {t('buttons.add')}
        </>
      ),
      onClick: () => handleAddClick(selectedProduct as IProduct),
    },
    {
      component: (
        <>
          <img src={trashCan} alt="Remove" />
          {t('buttons.remove')}
        </>
      ),
      onClick: () =>
        handleWishlist({
          id: selectedProductId,
          productTemplateId: selectedProduct?.categoryId || 0,
        }),
    },
  ]
}

export default Wishlist
